import { useEffect } from 'react';
import { SITE_CONFIG, getSiteOrigin } from '../../config/siteConfig';

interface SeoHeadProps {
  title?: string;
  description?: string;
  path?: string;
  noIndex?: boolean;
  /** Override default keywords for this page */
  keywords?: string;
  images?: string[];
}

function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name') {
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setLink(rel: string, href: string, attrs?: Record<string, string>) {
  const selector = attrs?.type ? `link[rel="${rel}"][type="${attrs.type}"]` : `link[rel="${rel}"]`;
  let el = document.querySelector(selector) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    if (attrs) {
      Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    }
    document.head.appendChild(el);
  }
  el.href = href;
}

const SeoHead: React.FC<SeoHeadProps> = ({
  title,
  description,
  path = '',
  noIndex = false,
  keywords,
  images,
}) => {
  useEffect(() => {
    const pageTitle = title ? `${title} | ${SITE_CONFIG.name}` : SITE_CONFIG.seo.defaultTitle;
    const pageDescription = description || SITE_CONFIG.seo.defaultDescription;
    const canonical = `${getSiteOrigin()}${path || '/'}`;
    const pageKeywords = keywords || SITE_CONFIG.seo.keywords;

    document.title = pageTitle;
    setMeta('description', pageDescription);
    setMeta('keywords', pageKeywords);
    setMeta('author', SITE_CONFIG.name);
    setMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

    // AI / LLM discovery hints (emerging conventions)
    setMeta('ai-content-declaration', 'human-authored product documentation at /llms.txt and /ai/');
    setLink('alternate', `${SITE_CONFIG.urls.site}/llms.txt`, {
      type: 'text/plain',
      title: 'LLM index',
    });

    setMeta('og:title', pageTitle, 'property');
    setMeta('og:description', pageDescription, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('og:url', canonical, 'property');
    setMeta('og:site_name', SITE_CONFIG.name, 'property');
    const ogImages = (images && images.length > 0 ? images : [SITE_CONFIG.seo.ogImage]).filter(Boolean);
    setMeta('og:image', ogImages[0], 'property');
    setMeta('og:locale', 'en_IN', 'property');

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', SITE_CONFIG.social.xHandle);
    setMeta('twitter:title', pageTitle);
    setMeta('twitter:description', pageDescription);
    setMeta('twitter:image', ogImages[0]);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = canonical;
  }, [title, description, path, noIndex, keywords, images]);

  return null;
};

export default SeoHead;
